import {
  nodeToLatex,
  serializeTextSlice,
  hasLatexMathBlocks,
  indexOfAnyCommand,
  getTextContent,
} from "@corca/latex";
import { Extension } from "@tiptap/core";
import {
  Fragment,
  DOMParser as PMDOMParser,
  DOMSerializer as PMDOMSerializer,
  type Node as ProsemirrorNode,
  Slice,
} from "@tiptap/pm/model";
import { Plugin, TextSelection } from "@tiptap/pm/state";
import { type NodeViewRendererProps } from "@tiptap/react";
import { prosemirrorToYDoc } from "@tiptap/y-tiptap";
import { isEqual } from "es-toolkit";
import { marked } from "marked";
import * as Y from "yjs";

import { matrixSelectionJson } from "@/components/expression/utils/matrixSelection";
import { freshCodeBlockRuntimeAttrs } from "@/components/tiptap/code-block/codeBlockRuntimeAttrs";
import {
  BYPASS_COLLAPSE_SELECTION_GUARD,
  collapsedCopyRange,
} from "@/components/tiptap/collapse/collapseCopy";
import { recallAppClipboardCopy } from "@/components/tiptap/copy-paste/appClipboardCopy";
import { deleteSelection } from "@/components/tiptap/utils/deleteSelection";
import {
  handleCut,
  serializeForClipboard,
  setClipboardData,
} from "@/components/tiptap/utils/handleCut";
import { replaceNodes } from "@/components/tiptap/utils/replaceNodes";
import { GraphNodeModel } from "@/models/nodes/GraphNodeModel";
import { TextVisitor } from "@/models/nodes/visitors/TextVisitor";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { teamPageUrl } from "@/urls";
import { LatexParser } from "@/utils/LatexParser";
import { MarkdownParser } from "@/utils/MarkdownParser";
import { normalizeMathText } from "@/utils/normalizeMathText";
import { trackEvent } from "@/utils/trackEvent";
import { trackNodesCreated } from "@/utils/trackNodeCreation";

import { isDocSelected } from "../utils/isDocSelected";

import { copyPasteKey } from "./copyPasteKey";
import { hydratePastedContent } from "./hydratePastedContent";
import { sanitizeDollarSigns } from "./sanitizeDollarSigns";

import type { PluginState, SelectionNodesJson } from "./copyPasteKey";
import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeJson, NodeJsonMap } from "@corca/data";
import type { EditorView } from "@tiptap/pm/view";
import type { Tokens } from "marked";

type CopyPasteOptions = NodeViewRendererProps & {
  doc: DocumentModel;
  uploadScope?: UploadScope;
};

export const CopyPaste = Extension.create<CopyPasteOptions>({
  addProseMirrorPlugins() {
    const { doc, uploadScope } = this.options;
    return [createCopyPastePlugin(doc, this.editor.schema, uploadScope)];
  },
});

// TODO: support truly async parsing
// (insert placeholder node, then replace it with parsed content when parsing is done)

let latexParser: LatexParser | null = null;
let markdownParser: MarkdownParser | null = null;

export async function initParser() {
  if (latexParser !== null) return;

  latexParser = await LatexParser.create();
  markdownParser = await MarkdownParser.create();
}

/**
 * Creates CopyPaste plugin that handles copying and pasting math block content
 * (that is not a part of ProseMirror content)
 * and parsing and pasting LaTeX formulas as math expressions as well.
 *
 * Note that clipboard can contain plain text, HTML text or image data.
 *
 * Pasted content is treated as "partial" when it is bare inner math nodes inserted inside a math
 * block, as opposed to entire math blocks, paragraphs or other content handled by ProseMirror.
 * Partialness is not stored on the clipboard; it is derived from the pasted node shape at paste
 * time (see hydratePastedContent).
 */
export function createCopyPastePlugin(
  doc: DocumentModel,
  schema: ProsemirrorNode["type"]["schema"],
  uploadScope?: UploadScope,
) {
  initParser();

  const createWrapperNodeJson = (children: NodeJson[]): NodeJsonMap["wrapper"] => {
    return {
      type: "wrapper",
      props: {},
      children: [
        {
          type: "inline",
          props: {},
          children,
        },
      ],
    };
  };

  const getNodeJson = (nodeId: string) => {
    const node = doc.nodes.get(nodeId);
    if (!node) {
      return null;
    }

    if (node.is(WrapperNodeModel)) {
      let wrapperNodeJson = node.toJSON() as NodeJsonMap["wrapper"];
      if (doc.matrixSelection.currentSelection?.node?.ancestors?.includes(node)) {
        const matrixJson = matrixSelectionJson(doc);
        if (matrixJson) {
          wrapperNodeJson = createWrapperNodeJson([matrixJson]);
        }
      } else {
        const selectedNodes = doc.selection.getSelectedNodesInWrapper(node);
        const selectedNodesJson = selectedNodes.map((n) => n.toJSON());
        if (
          selectedNodes.length > 0 &&
          !isEqual(wrapperNodeJson.children[0].children, selectedNodesJson)
        ) {
          wrapperNodeJson = createWrapperNodeJson(selectedNodesJson);
        }
      }
      return wrapperNodeJson;
    }

    return node.toJSON();
  };

  // Build a clipboard serializer that serializes inline-expression nodes
  // as <span> elements with LaTeX fallback text, so external apps
  // (Google Docs, Mac Notes, etc.) can display math content.
  const defaultSerializer = PMDOMSerializer.fromSchema(schema);
  const inlineExpressionSerializer = defaultSerializer.nodes.inlineExpression;
  const clipboardSerializer = inlineExpressionSerializer
    ? new PMDOMSerializer(
        {
          ...defaultSerializer.nodes,
          inlineExpression: (node, ...rest) => {
            const nodesJson = node.attrs.nodesJson as string | null;
            if (nodesJson) {
              try {
                const parsed = JSON.parse(nodesJson) as SelectionNodesJson;
                if (parsed.nodes?.[0]) {
                  const { text } = nodeToLatex(parsed.nodes[0]);
                  return [
                    "span",
                    {
                      "data-type": "inline-expression",
                      "data-node-id": node.attrs.nodeId as string,
                      "data-nodes-json": nodesJson,
                      "data-is-inline": String(node.attrs.isInline),
                    },
                    text,
                  ];
                }
              } catch {
                // ignore parse errors
              }
            }
            return inlineExpressionSerializer(node, ...rest);
          },
        },
        defaultSerializer.marks,
      )
    : undefined;

  return new Plugin<PluginState>({
    key: copyPasteKey,
    state: {
      init() {
        return { parsedNodes: null, context: "plain" };
      },
      apply(_, prev) {
        return prev;
      },
    },
    props: {
      clipboardSerializer,

      handleDOMEvents: {
        copy(view, event) {
          const { from, to } = view.state.selection;
          const range = collapsedCopyRange(view.state, from, to);

          if (range.from === from && range.to === to) {
            return false;
          }

          return setClipboardData(
            event,
            serializeForClipboard(view, view.state.doc.slice(range.from, range.to)),
          );
        },
        cut(view, event) {
          return doc.transact(() => {
            const { from, to } = view.state.selection;
            const range = collapsedCopyRange(view.state, from, to);

            if (range.from !== from || range.to !== to) {
              // Widen the selection to cover the whole collapsed section so the normal cut path
              // acts on it — `handleCut` copies the range to the clipboard and `deleteSelection`
              // removes it. This selection spans hidden content and bypasses the guard that would
              // otherwise shrink it back to a NodeSelection, but it's throwaway: the two calls
              // below consume it in this same transaction, so it never surfaces as a real selection.
              view.dispatch(
                view.state.tr
                  .setSelection(TextSelection.create(view.state.doc, range.from, range.to))
                  .setMeta(BYPASS_COLLAPSE_SELECTION_GUARD, true),
              );
            }

            handleCut(view, event);
            deleteSelection(doc, view);
            return true;
          }, "CopyPaste:cut");
        },
      },

      clipboardTextSerializer(slice, view) {
        const isMathOnly =
          slice.size === 1 &&
          (doc.selection.hasSelectedNodes || doc.matrixSelection.currentSelection);

        if (isMathOnly) {
          const { selectedNodes } = doc.selection;
          const selectedNodesTexts = selectedNodes.map((node) => {
            return nodeToLatex(node.toJSON()).text;
          });
          const matrixJson = matrixSelectionJson(doc);
          if (matrixJson) {
            selectedNodesTexts.push(nodeToLatex(matrixJson).text);
          }

          return selectedNodesTexts.join(" ");
        }

        // serializeTextSlice() deals with Y.XmlFragment, so we need to convert ProseMirror node to it first

        const sliceDoc = view.state.schema.topNodeType.createAndFill(null, slice.content);
        if (!sliceDoc) return "";
        const yDoc = prosemirrorToYDoc(sliceDoc);

        return serializeTextSlice({
          content: yDoc.getXmlFragment("prosemirror"),
          context: "plain",
          getNodeJson,
          serializeNode: (node, context) => {
            if (context === "plain") {
              const expressionText = serializeInlineExpressionToPlainText(doc, node);

              if (expressionText) {
                return { text: expressionText };
              }
            }

            return undefined;
          },
        }).text;
      },

      /**
       * Injects math block content into ProseMirror node attribute,
       * so it can be copy-pasted as HTML data
       */
      transformCopied(slice, view) {
        const newSlice = new Slice(
          mapFragment(slice.content, (node, getContent) => {
            if (node.type.name === "documentTitle") {
              if (isDocSelected(view)) {
                const bylineNode = createTeamBylineNode(schema, doc);
                return bylineNode ? [node, bylineNode] : node;
              }

              return node;
            }

            if (node.type.name === "text") {
              return node;
            }

            if (node.type.name === "image" || node.type.name === "fileAttachment") {
              const hasSource = node.attrs.sourceUid != null;
              return node.type.create(
                {
                  ...node.attrs,
                  sourceUid: hasSource
                    ? (node.attrs.sourceUid as string)
                    : (uploadScope?.uid ?? doc.id),
                  sourceType: hasSource
                    ? (node.attrs.sourceType as string | null)
                    : (uploadScope?.type ?? "document"),
                },
                getContent(),
                node.marks,
              );
            }

            if (node.type.name === "graph") {
              const graphNode = doc.nodes.get(node.attrs.graphId as string);
              const graphNodesJson: SelectionNodesJson = {
                nodes: graphNode?.is(GraphNodeModel) ? [graphNode.toJSON()] : [],
              };

              return node.type.create(
                {
                  ...node.attrs,
                  graphNodesJson: JSON.stringify(graphNodesJson),
                },
                getContent(),
                node.marks,
              );
            }

            const wrapperNodeJson = getNodeJson(node.attrs.nodeId as string);
            const nodes = wrapperNodeJson ? [wrapperNodeJson] : [];

            const selectedNodesJson = {
              nodes,
            };

            return node.type.create(
              {
                ...node.attrs,
                nodesJson: JSON.stringify(selectedNodesJson),
              },
              getContent(),
              node.marks,
            );
          }),
          slice.openStart,
          slice.openEnd,
        );

        return newSlice;
      },

      /**
       * If pasted text content looks like LaTeX, replaces the slice with LaTeX formulas
       * parsed into math nodes.
       *
       * Content is treated as "partial" if it is inserted inside math block.
       */
      clipboardTextParser(text, _context, _plain, view) {
        const isOutsideExpression = !doc.cursor.isActive;
        const state = this.getState(view.state)!;

        return parseMarkdownToSlice(text, view, isOutsideExpression, state) as unknown as Slice;
      },

      /**
       * treats pasted HTML content (as opposed to plain text content)
       * Detects pasted ProseMirror content (wrapped in <p data-pm-slice>)
       * and if it has only plain text inside, parses it just like clipboardTextParser() above does.
       */
      transformPastedHTML(pastedHtml, view) {
        const html = recallAppClipboardCopy(pastedHtml) ?? pastedHtml;
        const isOutsideExpression = !doc.cursor.isActive;
        const domDoc = new DOMParser().parseFromString(html, "text/html");
        const content = traversePastedDomElement(domDoc, domDoc.documentElement).textContent ?? "";

        let hasBlocks = hasSupportedHTMLBlocks(domDoc.documentElement);

        if (html.includes('"katex-html"')) {
          // TEMP FIX: allow latex convertion if there's a katex mathml content
          // this will work incorrectly with this code: `<math>...<math> <code>$ x + y $</code>`
          // TODO: convert only the content inside of <math> tag
          // @see https://linear.app/corca-research/issue/D-1446/improve-latex-parsing-in-pasted-html
          hasBlocks = false;
        }

        const shouldParseLatex = isOutsideExpression
          ? !hasBlocks && hasLatexMathExpressions(content)
          : !hasBlocks;

        if (latexParser && shouldParseLatex) {
          const domParser = PMDOMParser.fromSchema(view.state.schema);
          const slice = createLatexSlice(
            this.getState(view.state),
            latexParser,
            domParser,
            content,
            isOutsideExpression,
          );

          if (!slice) return html;

          const el = PMDOMSerializer.fromSchema(view.state.schema).serializeFragment(slice.content);

          if (el.firstChild instanceof HTMLElement) {
            // @see https://github.com/ProseMirror/prosemirror-view/blob/master/src/clipboard.ts#L34
            el.firstChild.setAttribute("data-pm-slice", `${slice.openStart} ${slice.openEnd} []`);
          }
          return new XMLSerializer().serializeToString(el);
        }

        return html;
      },

      /**
       * Transforms pasted slice, extracting nodes data from Prosemirror node attribute.
       * Should not have side effects (other than storing parsed nodes in plugin state)
       *
       * new (top level) node ids are created here, as it stays in ProseMirror node attributes and
       * should be a part of slice content.
       */
      transformPasted(slice, view) {
        if (slice.size === 0) return slice;

        const pastedSlice = isPastingIntoDocumentTitle(view)
          ? stripTeamBylineFromSlice(slice, doc)
          : slice;

        const parsedNodes: SelectionNodesJson[] = [];
        const isOutsideExpression = !doc.cursor.isActive;

        // If current selection is inside code (inline `code` mark or `codeBlock`) or a link mark,
        // we want to keep pasted content as-is and avoid converting it to math via replaceNodes().
        const {
          selection: { $from },
        } = view.state;
        const isInsideInlineCode = selectionHasCodeMark(view);
        const isInsideLinkMark = selectionHasLinkMark(view);
        const skipMathConversionBlock = Array.from({ length: $from.depth + 1 }).some((_, depth) => {
          const node = $from.node(depth);
          return node.type.name === "codeBlock";
        });
        const skipMathConversion =
          isInsideInlineCode || skipMathConversionBlock || isInsideLinkMark;

        const trimmedSlice = isOutsideExpression ? pastedSlice : trimSlice(pastedSlice);

        const convertToPartial = !isOutsideExpression && hasSingleExpressionInSlice(trimmedSlice);

        let unwrappedChangeRequest = false;

        const newSlice = new Slice(
          mapFragment(trimmedSlice.content, function transformPastedNode(node, getContent) {
            if (node.type.name === "changeRequest") {
              unwrappedChangeRequest = true;
              return fragmentToArray(
                mapFragment(getVisibleChangeRequestContent(node), transformPastedNode),
              );
            }
            if (node.type.name === "changeRequestPart") {
              unwrappedChangeRequest = true;
              return fragmentToArray(getContent());
            }
            if (node.type.name === "codeBlock") {
              // Keep children untouched: mapping them would run math conversion on the
              // code text, producing inlineExpression nodes that violate codeBlock's
              // text-only schema (the server then silently deletes the block).
              return node.type.create(
                freshCodeBlockRuntimeAttrs(node.attrs, view.state),
                node.content,
                node.marks,
              );
            }
            if (node.type.name === "heading") {
              return node;
            }
            if (node.type.name === "text") {
              // When pasting into inline code, force all text to carry the `code` mark
              // so that the whole pasted content stays inside the inline code span.
              if (isInsideInlineCode) {
                const codeMarkType = view.state.schema.marks.code;
                if (codeMarkType && !node.marks.some((m) => m.type.name === "code")) {
                  return node.mark([codeMarkType.create()]);
                }
                return node;
              }

              if (skipMathConversion) {
                return node;
              }

              if (node.marks.some((m) => m.type.name === "code" || m.type.name === "link")) {
                return node;
              }

              const { nodesJson, nodes } = replaceNodes(node);

              if (nodesJson.length > 0) {
                parsedNodes.push({
                  nodes: nodesJson,
                });
              }
              return nodes;
            }
            if (node.type.name === "graph") {
              const graphNodesJson = parseClipboardNodes(node.attrs.graphNodesJson as string);
              let graphId = node.attrs.graphId as string;

              if (graphNodesJson.nodes.length > 0) {
                graphId = doc.generateNodeId();
                graphNodesJson.nodes[0].id = graphId;
              }

              parsedNodes.push(graphNodesJson);

              return node.type.create(
                {
                  ...node.attrs,
                  graphId,
                  graphNodesJson: undefined,
                },
                getContent(),
                node.marks,
              );
            }
            if (node.attrs.nodesJson) {
              const nodesJson = parseClipboardNodes(node.attrs.nodesJson as string);
              let nodeId = node.attrs.nodeId as string;

              if (convertToPartial) {
                nodesJson.nodes = nodesJson.nodes[0].children[0].children;
              } else if (nodesJson.nodes.length > 0) {
                nodeId = doc.generateNodeId();
                nodesJson.nodes[0].id = nodeId;
              } else {
                console.error("empty nodes in node attrs:", node.type.name, node.attrs.nodesJson);
                return undefined;
              }

              parsedNodes.push(nodesJson);

              return node.type.create(
                {
                  ...node.attrs,
                  nodeId,
                  nodesJson: undefined,
                },
                getContent(),
                node.marks,
              );
            }

            let { attrs } = node;

            if (
              (node.type.name === "image" || node.type.name === "fileAttachment") &&
              attrs.sourceUid === (uploadScope?.uid ?? doc.id)
            ) {
              attrs = { ...attrs, sourceUid: null, sourceType: null };
            }
            return node.type.create(attrs, getContent(), node.marks);
          }),
          unwrappedChangeRequest ? 0 : trimmedSlice.openStart,
          unwrappedChangeRequest ? 0 : trimmedSlice.openEnd,
        );

        const pluginState = this.getState(view.state);
        if (pluginState) {
          if (!pluginState.parsedNodes) pluginState.parsedNodes = [];
          pluginState.parsedNodes.push(...parsedNodes);
        }

        return newSlice;
      },

      /**
       * Handles drag n drop event
       * if moved = false, original content is preserved (drag n drop with Ctrl pressed)
       */
      handleDrop(view, _event, _slice, moved) {
        const pluginState = this.getState(view.state);

        return hydratePastedContent(
          pluginState,
          doc,
          null,
          view,
          moved ? "moved" : "duplicated",
          uploadScope,
        );
      },

      handlePaste(view, event) {
        const pluginState = this.getState(view.state);
        return hydratePastedContent(
          pluginState,
          doc,
          event.clipboardData,
          view,
          "pasted",
          uploadScope,
        );
      },
    },
  });
}

function hasLatexMathExpressions(text: string) {
  return hasLatexMathBlocks(text) || (text.includes("\\") && indexOfAnyCommand(text)[0] !== -1);
}

const TEAM_BYLINE_SEPARATOR = " · @";

function teamBylineText(doc: DocumentModel) {
  const title = doc.team?.title;
  const permalink = doc.team?.permalink;

  if (!title || !permalink) {
    return null;
  }

  return `${title}${TEAM_BYLINE_SEPARATOR}${permalink}`;
}

function createTeamBylineNode(schema: ProsemirrorNode["type"]["schema"], doc: DocumentModel) {
  const text = teamBylineText(doc);
  const permalink = doc.team?.permalink;

  if (!text || !permalink) {
    return null;
  }

  const href = teamPageUrl({ teamPermalink: permalink }, { host: true });
  const linkMark = schema.marks.link?.create({ href });
  const textNode = linkMark ? schema.text(text, [linkMark]) : schema.text(text);

  return schema.nodes.paragraph.create(null, textNode);
}

function isPastingIntoDocumentTitle(view: EditorView) {
  const { $from } = view.state.selection;

  for (let { depth } = $from; depth >= 0; depth -= 1) {
    if ($from.node(depth).type.name === "documentTitle") {
      return true;
    }
  }

  return false;
}

function stripTeamBylineFromSlice(slice: Slice, doc: DocumentModel) {
  const bylineText = teamBylineText(doc);
  if (!bylineText) {
    return slice;
  }

  const nodes: ProsemirrorNode[] = [];
  let afterTitle = false;

  slice.content.forEach((node: ProsemirrorNode) => {
    const isByline =
      afterTitle && node.type.name === "paragraph" && node.textContent === bylineText;

    if (!isByline) {
      nodes.push(node);
    }

    afterTitle = node.type.name === "documentTitle";
  });

  return new Slice(Fragment.fromArray(nodes), slice.openStart, slice.openEnd);
}

function mapFragment(
  fragment: Fragment,
  cb: (
    node: ProsemirrorNode,
    getContent: () => Fragment,
  ) => ProsemirrorNode | ProsemirrorNode[] | undefined,
) {
  const nodes: ProsemirrorNode[] = [];
  fragment.forEach((node) => {
    const newNode = cb(node, () => mapFragment(node.content, cb));
    if (newNode !== undefined) {
      if (Array.isArray(newNode)) {
        nodes.push(...newNode);
      } else {
        nodes.push(newNode);
      }
    }
  });
  return Fragment.fromArray(nodes);
}

function fragmentToArray(fragment: Fragment): ProsemirrorNode[] {
  const nodes: ProsemirrorNode[] = [];
  fragment.forEach((node) => nodes.push(node));
  return nodes;
}

function getVisibleChangeRequestContent(node: ProsemirrorNode): Fragment {
  let beforeContent: Fragment | undefined;
  let afterContent: Fragment | undefined;

  node.forEach((child) => {
    if (child.type.name !== "changeRequestPart") {
      return;
    }

    if (child.attrs.kind === "before") {
      beforeContent = child.content;
    } else {
      afterContent = child.content;
    }
  });

  return afterContent ?? beforeContent ?? Fragment.empty;
}

function parseClipboardNodes(nodesJson: string): SelectionNodesJson {
  try {
    const parsed = JSON.parse(nodesJson) as SelectionNodesJson;

    return parsed;
  } catch (e) {
    console.error(e);
  }
  return { nodes: [] };
}

/**
 * Track variables, constants, functions, operator symbols, groups, and numbers created from LaTeX import
 * Fires nodes_created_from_latex_import once per import action, and *_created events once per session per type
 */
function trackLatexNodes(nodesJson: NodeJson[]) {
  trackEvent("nodes_created_from_latex_import", {});
  trackNodesCreated(nodesJson, "latex_import");
}

function createLatexSlice(
  pluginState: PluginState | undefined,
  parser: LatexParser,
  domParser: PMDOMParser,
  text: string,
  isOutsideExpression: boolean,
) {
  // Sanitize dollar signs followed by numbers to avoid misparsing them as inline math
  const sanitizedText = sanitizeDollarSigns(text);
  const normalizedText = normalizeMathText(sanitizedText);
  const res = parser.parse(normalizedText, !isOutsideExpression);

  if (res.errors.find(([nodeType]) => nodeType === "ERROR")) {
    return undefined;
  }

  trackLatexNodes(res.nodes);

  const dom = replaceMarkdownInDom(res.dom);

  const slice = domParser.parseSlice(dom);

  if (pluginState) {
    const convertToPartial = !isOutsideExpression && hasSingleExpressionInSlice(slice);

    if (convertToPartial) {
      pluginState.parsedNodes = [
        {
          nodes: res.nodes[0].children[0].children,
        },
      ];
    } else {
      if (!pluginState.parsedNodes) pluginState.parsedNodes = [];
      pluginState.parsedNodes.push({
        nodes: res.nodes,
      });
    }
  }

  return slice;
}

function selectionHasCodeMark(view: EditorView) {
  const { state } = view;
  const marks = state.storedMarks ?? state.selection.$from.marks();
  return !!marks && marks.some((m) => m.type.name === "code");
}

function selectionHasLinkMark(view: EditorView) {
  const { state } = view;
  const marks = state.storedMarks ?? state.selection.$from.marks();
  return !!marks && marks.some((m) => m.type.name === "link");
}

function hasSingleExpressionInSlice(slice: Slice) {
  const nodes: ProsemirrorNode[] = [];
  slice.content.forEach((n) => nodes.push(n));

  let expressionCount = 0;
  for (const node of nodes) {
    if (node.type.name === "inline-expression") {
      expressionCount += 1;
      if (expressionCount > 1) return false;
    } else if (node.textContent.trim().length > 0) {
      return false;
    }
  }
  return true;
}

function isTextNode(node: ProsemirrorNode) {
  return ["paragraph", "documentTitle", "h1", "h2", "h3", "h4", "h5", "h6"].includes(
    node.type.name,
  );
}

function trimSlice(slice: Slice) {
  const nodes: ProsemirrorNode[] = [];
  if (
    slice.content.childCount === 1 &&
    slice.content.firstChild &&
    isTextNode(slice.content.firstChild)
  ) {
    slice.content.firstChild.content.forEach((n) => nodes.push(n));
  } else {
    slice.content.forEach((n) => nodes.push(n));
  }

  const nonEmptyStart = nodes.findIndex((n) => {
    return !isTextNode(n) || n.textContent.trim().length > 0;
  });
  const nonEmptyEnd = nodes.findLastIndex((n) => {
    return !isTextNode(n) || n.textContent.trim().length > 0;
  });

  const trimmedNodes = nodes.slice(nonEmptyStart, nonEmptyEnd + 1);

  if (trimmedNodes.length === 1 && isTextNode(trimmedNodes[0])) {
    return new Slice(trimmedNodes[0].content, slice.openStart, slice.openEnd);
  }

  return new Slice(Fragment.fromArray(trimmedNodes), slice.openStart, slice.openEnd);
}

const supportedTags = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "code",
  "inline-expression",
  "img",
  "ul",
  "ol",
  "blockquote",
  "hr",
];

function hasSupportedHTMLBlocks(element: HTMLElement) {
  for (const node of element.children) {
    const tagName = node.tagName.toLowerCase();
    if (supportedTags.includes(tagName)) return true;
    if (node instanceof HTMLElement) {
      if (node.dataset.type === "inline-expression") return true;
      if (hasSupportedHTMLBlocks(node)) return true;
    }
  }
  return false;
}

function traversePastedDomElement(doc: Document, element: HTMLElement) {
  for (const node of element.children) {
    const tagName = node.tagName.toLowerCase();

    if (tagName === "math") {
      const span = doc.createElement("span");
      const texElement = node.querySelector("annotation[encoding='application/x-tex']");
      span.innerHTML = `$ ${texElement?.textContent} $`;
      element.replaceChild(span, node);
    } else if (node.className === "katex-html") {
      element.removeChild(node);
    } else if (node instanceof HTMLElement) {
      traversePastedDomElement(doc, node);
    }
  }

  return element;
}

marked.use({
  renderer: {
    heading({ tokens, depth }: Tokens.Heading) {
      const headingDepth = Math.min(depth + 1, 6);
      return `<h${headingDepth}>${this.parser.parseInline(tokens)}</h${headingDepth}>\n`;
    },
  },
});

/**
 * Parses markdown text into ProseMirror Slice
 * - markdown includes parsing LaTeX blocks and inline formulas (to consider: Obsidian only supports block LaTeX)
 * - if no markdown is found, but LaTeX expressions are detected, parses LaTeX only
 */
function parseMarkdownToSlice(
  text: string,
  view: EditorView,
  isOutsideExpression: boolean,
  state: PluginState,
) {
  if (!latexParser || !markdownParser) return undefined;

  const normalizedText = normalizeMathText(text);
  const { text: html, nodes, latexFound } = markdownParser.parse(normalizedText);

  trackLatexNodes(nodes);

  if (nodes) {
    if (state.parsedNodes === null) {
      state.parsedNodes = [{ nodes }];
    } else {
      state.parsedNodes.push({ nodes });
    }
  }

  if (latexFound || (html.includes("<") && html.includes(">"))) {
    const dom = new DOMParser().parseFromString(html, "text/html");
    const domParser = PMDOMParser.fromSchema(view.state.schema);
    return domParser.parseSlice(dom.body);
  }

  const shouldParseLatex = isOutsideExpression ? hasLatexMathExpressions(text) : true;

  if (shouldParseLatex) {
    const domParser = PMDOMParser.fromSchema(view.state.schema);
    return createLatexSlice(state, latexParser, domParser, text, isOutsideExpression);
  }

  return undefined;
}

function replaceMarkdownInDom(dom: Document) {
  const walker = dom.createTreeWalker(
    dom.body,
    // eslint-disable-next-line no-bitwise
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    null,
  );

  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();
  while (currentNode) {
    if (currentNode.nodeType === Node.TEXT_NODE) {
      textNodes.push(currentNode as Text);
    }
    currentNode = walker.nextNode();
  }

  for (const textNode of textNodes) {
    const { parentElement, textContent } = textNode;
    if (parentElement && textContent && markdownParser) {
      const { text: html } = markdownParser.parse(textContent);
      if (html.trim() !== textContent.trim()) {
        const span = dom.createElement("span");
        span.innerHTML = html;
        parentElement.replaceChild(span, textNode);
      }
    }
  }

  return dom;
}

function shouldSerializeExpressionToText(node?: WrapperNodeModel): node is Maybe<WrapperNodeModel> {
  const { nodes = [] } = node?.inlineNode ?? {};

  if (nodes.length === 1) {
    const [firstNode] = nodes;

    switch (firstNode.type) {
      case "variable":
      case "constant":
      case "operatorSymbol": {
        if (firstNode.symbolNode) {
          return !firstNode.symbolNode.hasLabels();
        }

        return false;
      }

      case "number":
        return true;

      default:
        return false;
    }
  }

  return false;
}

function serializeInlineExpressionToPlainText(
  doc: DocumentModel,
  node: Y.XmlElement<Record<string, string>>,
) {
  const { parent } = node;
  const nodeId = node.getAttribute("nodeId");

  if (!nodeId || node.nodeName !== "inlineExpression") {
    return undefined;
  }

  if (parent && parent instanceof Y.XmlElement) {
    const hasTextSiblings =
      getTextContent(parent as Y.XmlElement<Record<string, string>>).trim().length > 0;

    if (!hasTextSiblings) {
      return undefined;
    }

    const nodeModel = doc.nodes.get(nodeId) as WrapperNodeModel | undefined;

    if (shouldSerializeExpressionToText(nodeModel)) {
      const textVisitor = new TextVisitor();
      return nodeModel.accept(textVisitor);
    }
  }

  return undefined;
}
